import { Group, ReadinessGaugeResultDetail, Resource } from '@esg/esg-global-types';
import { generateUuidV4 } from '../../util/uuid';
import { Box, ButtonBase, Grid, Typography } from '@mui/material';
import React from 'react';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import {
  ReadinessGaugeExtended,
  ResponseExtended,
  ResultDetailExtended,
  getReadinessGaugeResults
} from '../../lib/readiness_gauge/readiness_gauge_instance';
import { useLocation, Location, NavigateFunction, useNavigate } from 'react-router-dom';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { UserContext } from '../../context/UserContext';
import GaugeChart from '../../components/shared/charts/GaugeChart';
import { ReactComponent as PDFIcon } from '../../assets/icons/PDF.svg';
import { ReactComponent as ContactIcon } from '../../assets/icons/Contact.svg';
import CategoryResultsSection, {
  CategoryResults
} from '../../components/readiness_gauge/results/CategoryResults';
import { KPIExtended } from '../../lib/readiness_gauge/kpi';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import { generatePdf } from '../../lib/pdf_export/readiness_pdf';
import GhostButton from '../../components/shared/button/GhostButton';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Title } from '../../components/shared/typography/PrimaryTypography';
import PrimarySkeleton from '../../components/shared/loading/PrimarySkeleton';
import SkeletonGrid from '../../components/shared/loading/SkeletonGrid';
import { getResources } from '../../graphql/services/resource.service';
import { GaugeScores } from '../../lib/pdf_export/utils/readiness_pdf_conference';
import { CompanyContext } from '../../context/CompanyContext';

/**
 * View for displaying Readiness Gauge Results.
 * @returns {JSX.Element}
 */
const ReadinessGaugeResults = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { company } = React.useContext(CompanyContext);

  if (!company) {
    return null;
  }

  const [group, setGroup] = React.useState<Group | null>(null);
  const [readinessGauge, setReadinessGauge] = React.useState<ReadinessGaugeExtended | null>(null);
  const [resultsLoading, setResultsLoading] = React.useState<boolean>(true);
  const [category, setCategory] = React.useState<string>('');
  const [exportLoading, setExportLoading] = React.useState<boolean>(false);
  const [griResources, setGRIResources] = React.useState<Array<Resource>>([]);
  const [sdgResources, setSDGResources] = React.useState<Array<Resource>>([]);

  const location: Location = useLocation();
  const navigate: NavigateFunction = useNavigate();

  const total_readiness: number = readinessGauge?.result.summary
    ? (readinessGauge.result.summary?.overall.compliance_score /
        readinessGauge.result.summary?.overall.total_score) *
      100
    : 0;

  const readiness_text_color: string =
    total_readiness >= 90
      ? '#388e3c'
      : total_readiness >= 80
        ? '#FFD500'
        : total_readiness >= 50
          ? '#ffa726'
          : '#f44336';

  const environmental_readiness: number = readinessGauge?.result.summary
    ? (readinessGauge.result.summary?.environment.compliance_score /
        readinessGauge.result.summary?.environment.total_score) *
      100
    : 0;

  const social_readiness: number = readinessGauge?.result.summary
    ? (readinessGauge.result.summary?.social.compliance_score /
        readinessGauge.result.summary?.social.total_score) *
      100
    : 0;

  const governance_readiness: number = readinessGauge?.result.summary
    ? (readinessGauge.result.summary?.governance.compliance_score /
        readinessGauge.result.summary?.governance.total_score) *
      100
    : 0;

  const category_kpi_details: Array<ResultDetailExtended> =
    category && readinessGauge?.result.detail
      ? readinessGauge.result.detail.map((kpi_detail: ReadinessGaugeResultDetail) => ({
          ...kpi_detail,
          kpi: kpi_detail.kpi as KPIExtended,
          responses: readinessGauge.responses.filter(
            (response: ResponseExtended) =>
              response.question.kpi.id === kpi_detail.kpi.id &&
              response.question.categories
                .map((category: string) => category.toLowerCase())
                .includes(category.toLowerCase())
          )
        }))
      : [];

  const category_results: CategoryResults = {
    name: category,
    kpi_details: category_kpi_details.filter(
      (kpi_detail: ResultDetailExtended) => kpi_detail.responses.length > 0
    )
  };
  const handleChangeCategory = (new_category: string): void => {
    setCategory(new_category);
  };
  const handleExportPdf = async (): Promise<void> => {
    setExportLoading(true);
    const scores: GaugeScores = {
      environmental: Math.round(environmental_readiness),
      social: Math.round(social_readiness),
      governance: Math.round(governance_readiness),
      overall: Math.round(total_readiness)
    };
    await generatePdf(company.id, readinessGauge?.name, readinessGauge?.responses ?? [], scores);
    setExportLoading(false);
    return;
  };

  const handleContact = (): void => {
    navigate('/contact');
  };

  const handleBackToQuestionnaires = (): void => {
    navigate('/sustainability/readiness');
  };

  const handleGetResults = async (
    group: Group,
    readiness_gauge: ReadinessGaugeExtended
  ): Promise<void> => {
    try {
      await (async () => {
        setResultsLoading(true);
        if (group) {
          const readiness_gauge_with_results: ReadinessGaugeExtended =
            await getReadinessGaugeResults(group.id, readiness_gauge, readiness_gauge.responses);
          setReadinessGauge({
            ...readiness_gauge,
            result: readiness_gauge_with_results.result,
            responses: readiness_gauge_with_results.responses
          });
        }
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ReadinessGaugeResults failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setResultsLoading(false);
    }
  };

  React.useEffect(() => {
    if (readinessGauge?.result.summary) {
      // Set the initial category details to the lowest scoring one, defaulting to environmental.
      const category_scores: Map<string, number> = new Map([
        ['environmental', environmental_readiness],
        ['social', social_readiness],
        ['governance', governance_readiness]
      ]);

      const lowest_category: string = Array.from(category_scores.entries()).reduce(
        (lowest_category: string, [category, score]: [string, number]) =>
          score < category_scores.get(lowest_category)! ? category : lowest_category,
        'environmental'
      );
      setCategory(lowest_category);
    }
  }, [readinessGauge]);

  React.useEffect(() => {
    (async () => {
      if (location.state.group) {
        setGroup(location.state.group);
        await handleGetResults(location.state.group, location.state.readinessGauge);
        const gri_resources: Array<Resource> = await getResources({
          alias_substring_match: 'gri'
        });
        const sdg_resources: Array<Resource> = await getResources({
          alias_substring_match: 'sdg'
        });
        setGRIResources(gri_resources);
        setSDGResources(sdg_resources);
      } else {
        navigate('/sustainability/readiness');
      }
    })();
  }, []);

  return !user || !group ? (
    <LoadingScreen />
  ) : (
    <Box>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
            <Grid
              size={12}
              sx={{
                textAlign: 'center'
              }}
            >
              {resultsLoading ? (
                <PrimarySkeleton
                  variant="rounded"
                  sx={{ ml: 'auto', mr: 'auto', mt: 4 }}
                  width={'60%'}
                  height={80}
                />
              ) : (
                <Title id="report-name" weight="Bold" sx={{ mt: 4 }}>
                  {readinessGauge?.name + ' Results'}
                </Title>
              )}
            </Grid>
            <Grid size={11}>
              <Box
                id="total-readiness-gauge"
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                {resultsLoading ? (
                  <PrimarySkeleton variant="circular" width={350} height={350} />
                ) : (
                  <>
                    <GaugeChart
                      id="total-readiness-gauge-chart"
                      width={350}
                      height={250}
                      start_angle={-100}
                      end_angle={100}
                      value={total_readiness}
                      inner_radius={100}
                      show_readiness_gauge
                    />
                    <Typography
                      id="total-readiness-gauge-title"
                      color="primary"
                      sx={{ fontSize: '1.7rem', mb: 0.5 }}
                    >{`Your ESG Readiness Score`}</Typography>
                    <Box
                      id="total-readiness-gauge-percentage"
                      sx={{ backgroundColor: `${readiness_text_color}20`, borderRadius: 4, px: 4 }}
                    >
                      <Typography
                        sx={{ fontSize: '2rem', color: readiness_text_color, fontWeight: 500 }}
                      >
                        {`${Math.round(total_readiness)}%`}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid
                  size={2}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {resultsLoading ? (
                    <SkeletonGrid
                      skeleton_items={Array(3).fill({
                        size: 12,
                        variant: 'circular',
                        width: 150,
                        height: 150,
                        sx: { justifySelf: 'center' }
                      })}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <ButtonBase
                        id="environmental-readiness-gauge"
                        onClick={() => handleChangeCategory('environmental')}
                        sx={{
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        <GaugeChart
                          width={150}
                          height={150}
                          start_angle={-180}
                          end_angle={180}
                          value={environmental_readiness}
                          inner_radius={45}
                          text="E"
                          font_size={'2rem'}
                          value_arc_fill="#3F7E44"
                        />
                        <Box sx={{ backgroundColor: `#3F7E4420`, borderRadius: 2 }}>
                          <Typography
                            sx={{ fontSize: '1.5rem', fontWeight: 500, color: '#3F7E44' }}
                          >
                            {Math.round(environmental_readiness)}%
                          </Typography>
                        </Box>
                      </ButtonBase>
                      <ButtonBase
                        id="social-readiness-gauge"
                        onClick={() => handleChangeCategory('social')}
                        sx={{
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        <GaugeChart
                          width={150}
                          height={150}
                          start_angle={-180}
                          end_angle={180}
                          value={social_readiness}
                          font_size={'2rem'}
                          inner_radius={45}
                          text="S"
                          value_arc_fill="#FF3A21"
                        />
                        <Box sx={{ backgroundColor: `#FF3A2120`, borderRadius: 2 }}>
                          <Typography
                            sx={{ fontSize: '1.5rem', fontWeight: 500, color: '#FF3A21' }}
                          >
                            {Math.round(social_readiness)}%
                          </Typography>
                        </Box>
                      </ButtonBase>
                      <ButtonBase
                        id="governance-readiness-gauge"
                        onClick={() => handleChangeCategory('governance')}
                        sx={{
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        <GaugeChart
                          width={150}
                          height={150}
                          start_angle={-180}
                          end_angle={180}
                          value={governance_readiness}
                          font_size={'2rem'}
                          inner_radius={45}
                          text="G"
                          value_arc_fill="#00689D"
                        />
                        <Box sx={{ backgroundColor: `#00689D20`, borderRadius: 2 }}>
                          <Typography
                            sx={{ fontSize: '1.5rem', fontWeight: 500, color: '#00689D' }}
                          >
                            {Math.round(governance_readiness)}%
                          </Typography>
                        </Box>
                      </ButtonBase>
                    </Box>
                  )}
                </Grid>
                <Grid size={10}>
                  <CategoryResultsSection
                    loading={resultsLoading}
                    results={category_results}
                    gri_resources={griResources}
                    sdg_resources={sdgResources}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <GhostButton
                dark
                onClick={handleBackToQuestionnaires}
                startIcon={<KeyboardArrowLeftIcon />}
              >
                Questionnaires
              </GhostButton>
              <PrimaryButton
                startIcon={<PDFIcon height={35} stroke="currentColor" strokeWidth={2} />}
                onClick={async () => await handleExportPdf()}
                sx={{ minWidth: 150 }}
                loading={exportLoading}
              >
                Export PDF
              </PrimaryButton>
              <PrimaryButton
                onClick={handleContact}
                startIcon={<ContactIcon height={35} fill="white" stroke="white" />}
                sx={{ m: 2 }}
              >
                Contact Energy Partners
              </PrimaryButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReadinessGaugeResults;
